import { useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import ModalBody from 'react-bootstrap/ModalBody'
import { Calendar, DateObject } from 'react-multi-date-picker'
import persian from 'react-date-object/calendars/persian'
import persian_fa from 'react-date-object/locales/persian_fa'
import 'react-multi-date-picker/styles/colors/green.css'
import moment from 'jalali-moment'
import posthog from 'posthog-js'
import { FiCalendar, FiInfo } from 'react-icons/fi'
import useScoreFrom, { SCORE_FROM_MIN } from './useScoreFrom'
import { gregorianToLong } from './ScoreFromNote'

type TPreset = 'all' | 'year' | '12m' | 'custom'

const MIN_DATE = moment(SCORE_FROM_MIN, 'YYYY-MM-DD').toDate()

/**
 * The one-tap windows, computed from today every render so «امسال» turns
 * over on 1 Farvardin without anybody touching it.
 */
const presets = (): { key: TPreset; value: TScoreFrom }[] => {
    const today = moment()
    return [
        { key: 'all', value: null },
        {
            key: 'year',
            value: moment(`${today.jYear()}/1/1`, 'jYYYY/jM/jD').format(
                'YYYY-MM-DD'
            ),
        },
        {
            key: '12m',
            value: today.clone().subtract(12, 'months').format('YYYY-MM-DD'),
        },
    ]
}

const PILLS: { key: TPreset; label: string; aria: string }[] = [
    { key: 'all', label: 'ابتدا', aria: 'امتیاز از ابتدای صندوق' },
    { key: 'year', label: 'امسال', aria: 'امتیاز از ابتدای امسال' },
    { key: '12m', label: '۱۲ ماه', aria: 'امتیاز از دوازده ماه اخیر' },
    { key: 'custom', label: 'تاریخ', aria: 'امتیاز از تاریخ دلخواه' },
]

export const presetOf = (value: TScoreFrom): TPreset =>
    presets().find((preset) => preset.value === value)?.key ?? 'custom'

const daysBack = (value: TScoreFrom) =>
    value ? moment().diff(moment(value, 'YYYY-MM-DD'), 'days') : null

/**
 * «امتیاز از …» — pick the window the score is counted over, and the list
 * re-ranks on the spot (pwa#131). See `useScoreFrom` for why it is a filter
 * and not a saved setting.
 *
 * Laid out like the grant wizard's other filter rows (label, then equal
 * pills), because it is one: it changes what is on screen and nothing else.
 *
 * Renders nothing when `appliedFrom` is `undefined` — a backend that predates
 * the window sends no `start_date`, and would ignore `?score_from=` too.
 */
export default function ScoreFromFilter({
    moneypoolId,
    appliedFrom,
    busy = false,
    surface,
}: {
    moneypoolId?: string
    /** the window the numbers on screen were counted over, from the payload */
    appliedFrom: string | null | undefined
    /** a new window is being computed; the old numbers are still showing */
    busy?: boolean
    /** which screen it was changed from, for the funnel */
    surface: 'grant' | 'members'
}) {
    const [value, setValue] = useScoreFrom(moneypoolId)
    const [calendarOpen, setCalendarOpen] = useState(false)
    const [showHelp, setShowHelp] = useState(false)

    if (appliedFrom === undefined || !moneypoolId) return null

    const current = presetOf(value)

    const apply = (next: TScoreFrom) => {
        if (next === value) return
        posthog.capture('member_score_filter_changed', {
            fund_id: moneypoolId,
            surface,
            preset: presetOf(next),
            previous_preset: current,
            days_back: daysBack(next),
        })
        setValue(next)
    }

    const onPill = (key: TPreset) => {
        if (key === 'custom') {
            setCalendarOpen(true)
            posthog.capture('member_score_filter_calendar_opened', {
                fund_id: moneypoolId,
                surface,
                current_preset: current,
            })
            return
        }
        apply(presets().find((preset) => preset.key === key)?.value ?? null)
    }

    return (
        <>
            <div
                role="radiogroup"
                aria-label="امتیاز از چه تاریخی حساب شود"
                className=" mt-2 flex items-center gap-2"
            >
                <span className=" flex w-[68px] shrink-0 items-center gap-1 text-[11px] text-text-secondary">
                    امتیاز از
                    <button
                        type="button"
                        aria-label="راهنمای امتیاز از تاریخ"
                        aria-expanded={showHelp}
                        onClick={() => setShowHelp((open) => !open)}
                        className=" -m-1 p-1 text-text-muted"
                    >
                        <FiInfo size={13} />
                    </button>
                </span>
                <div className=" flex flex-1 items-center gap-1.5">
                    {PILLS.map((pill) => (
                        <button
                            key={pill.key}
                            type="button"
                            role="radio"
                            aria-checked={current === pill.key}
                            aria-label={pill.aria}
                            onClick={() => onPill(pill.key)}
                            className={` flex flex-1 items-center justify-center gap-1 rounded-full border py-1.5 text-[11px] font-bold transition-colors ${
                                current === pill.key
                                    ? ' border-brand bg-brand-soft text-brand-deep'
                                    : ' border-stroke text-text-secondary'
                            }`}
                        >
                            {pill.key === 'custom' && (
                                <FiCalendar size={12} aria-hidden />
                            )}
                            {pill.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* the day itself, in words: «امسال» and «۱۲ ماه» are relative,
                and a picked day does not fit inside a pill */}
            <p
                className=" mt-1 text-[11px] text-text-secondary"
                aria-live="polite"
            >
                {value ? (
                    <>
                        امتیاز و رتبه از{' '}
                        <b className=" text-text-body">
                            {gregorianToLong(value)}
                        </b>{' '}
                        حساب می‌شود
                    </>
                ) : (
                    'امتیاز و رتبه از ابتدای صندوق حساب می‌شود'
                )}
                {busy && (
                    <span className=" text-brand-deep"> — در حال محاسبه…</span>
                )}
            </p>

            {showHelp && (
                <div className=" mt-2 rounded-xl bg-canvas p-2.5 text-[11px] leading-5 text-text-secondary">
                    <ScoreFromHelp />
                </div>
            )}

            <Modal
                show={calendarOpen}
                onHide={() => setCalendarOpen(false)}
                dialogClassName="explainer-dialog"
                centered
                scrollable
                aria-labelledby="score-from-title"
            >
                <ModalBody className=" px-4 py-4">
                    <p
                        id="score-from-title"
                        className=" text-center text-sm font-bold text-heading"
                    >
                        امتیاز از چه تاریخی حساب شود؟
                    </p>
                    <p className=" mt-1 text-center text-[11px] text-text-secondary">
                        روزی را بزنید؛ فهرست همان لحظه دوباره مرتب می‌شود.
                    </p>
                    {/* Inline, not a popup behind an input: inside a
                        scrollable modal the popup is clipped by the body it
                        opens in, and the calendar IS the whole question. */}
                    <div className=" mt-3 flex flex-col items-center">
                        <Calendar
                            className="green"
                            calendar={persian}
                            locale={persian_fa}
                            value={
                                value
                                    ? moment(value, 'YYYY-MM-DD').toDate()
                                    : undefined
                            }
                            onChange={(date: DateObject | null) => {
                                if (!date) return
                                apply(
                                    moment(date.toDate()).format('YYYY-MM-DD')
                                )
                                setCalendarOpen(false)
                            }}
                            minDate={MIN_DATE}
                            maxDate={new Date()}
                        />
                    </div>
                    <div className=" mt-3 rounded-xl bg-canvas p-3 text-[11px] leading-6 text-text-secondary">
                        <ScoreFromHelp />
                    </div>
                </ModalBody>
                <div className=" flex shrink-0 px-4 pb-4 pt-2">
                    <button
                        type="button"
                        onClick={() => setCalendarOpen(false)}
                        className=" flex-1 rounded-full border border-stroke bg-white py-2.5 text-[15px] font-bold text-text-secondary"
                    >
                        بستن
                    </button>
                </div>
            </Modal>
        </>
    )
}

function ScoreFromHelp() {
    return (
        <>
            <p>
                چیزی پاک نمی‌شود. هرچه قبل از این تاریخ بوده، طوری حساب می‌شود
                که انگار همان روز اتفاق افتاده: پولی که آن روز در صندوق بوده از
                همان روز امتیاز می‌گیرد، و دیرکرد یا زودپرداختِ قبل از آن به
                حساب نمی‌آید.
            </p>
            <p className=" mt-1.5">
                قسطی که الان معوق است همچنان معوق نشان داده می‌شود. این فقط
                نمایش شماست و ذخیره نمی‌شود؛ بقیهٔ مدیران و اعضا امتیاز را مثل
                قبل می‌بینند.
            </p>
        </>
    )
}
